import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { useLanguage, scrollToId } from "../context/LanguageContext";
import { IMAGES } from "../data/images";

const MaskLine = ({ children, delay, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.15, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yMain = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const ySec = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const yTer = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden bg-travertine-light pt-[72px]">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(197,160,89,0.10),transparent_55%)]" />

      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 w-full grid lg:grid-cols-12 gap-10 lg:gap-6 items-center py-20 lg:py-0">
        <motion.div style={{ opacity: fade }} className="lg:col-span-7 relative z-10">
          <MaskLine delay={0.35}>
            <p data-testid="hero-overline" className="text-[11px] sm:text-xs font-mono uppercase tracking-[0.35em] text-gold mb-8">
              {t.hero.overline}
            </p>
          </MaskLine>

          <h1 data-testid="hero-title" className="tracking-tight">
            <span className="sr-only">Emanuela Serena — {t.hero.role}</span>
            <MaskLine delay={0.5}>
              <img
                data-testid="hero-mela-logo"
                src="/images/logo-mela.png"
                alt="Mela — marchio di Emanuela Serena"
                className="h-44 sm:h-56 lg:h-72 w-auto"
              />
            </MaskLine>
          </h1>
          <MaskLine delay={0.7} className="text-lg sm:text-2xl lg:text-3xl mt-8 font-sans font-light tracking-wide text-ink/80">
            — {t.hero.role}
          </MaskLine>

          <MaskLine delay={1.0} className="mt-10 max-w-xl">
            <p data-testid="hero-slogan" className="font-serif italic text-xl sm:text-2xl text-ink/85 leading-snug">
              “{t.hero.sloganPrimary}”
            </p>
            <p data-testid="hero-slogan-alt" className="mt-3 text-sm sm:text-base text-ink/55 leading-relaxed">
              {t.hero.sloganSecondary}
            </p>
          </MaskLine>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.3, ease: [0.22, 1, 0.36, 1] }}
            className="mt-12 flex flex-wrap items-center gap-5"
          >
            <button
              data-testid="hero-cta-explore"
              onClick={() => scrollToId("progetti")}
              className="group inline-flex items-center gap-3 bg-ink text-travertine-light px-8 py-4 rounded-full text-xs font-mono uppercase tracking-[0.25em] hover:bg-gold transition-colors duration-400"
            >
              {t.hero.ctaWorks}
              <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-cta-contact"
              onClick={() => scrollToId("contatti")}
              className="text-xs font-mono uppercase tracking-[0.25em] text-ink/70 border-b border-gold/60 pb-1 hover:text-gold transition-colors duration-300"
            >
              {t.hero.ctaContact}
            </button>
          </motion.div>
        </motion.div>

        <div className="lg:col-span-5 relative h-[420px] sm:h-[520px] lg:h-[640px] hidden md:block">
          <motion.div
            style={{ y: yMain }}
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.4, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="absolute top-0 right-0 w-[68%] frame-gold overflow-hidden rounded-[32px]"
          >
            <img src={IMAGES.heroMain} alt="Murano glass jewelry" className="w-full aspect-[3/4] object-cover" />
          </motion.div>
          <motion.div
            style={{ y: ySec }}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.3, delay: 1.0, ease: [0.22, 1, 0.36, 1] }}
            className="absolute bottom-4 left-0 w-[46%] frame-gold overflow-hidden rounded-[32px]"
          >
            <img src={IMAGES.heroSecondary} alt="BAB leather bags" className="w-full aspect-[4/5] object-cover" />
          </motion.div>
          <motion.div
            style={{ y: yTer }}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.3, delay: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="absolute bottom-[10%] left-[34%] w-[28%] z-10 frame-gold overflow-hidden rounded-[32px]"
          >
            <img src={IMAGES.heroTertiary} alt="Fine rings" className="w-full aspect-square object-cover" />
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-ink/40"
      >
        <span className="text-[10px] font-mono uppercase tracking-[0.35em]">{t.hero.scrollHint}</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}>
          <ArrowDown size={14} />
        </motion.span>
      </motion.div>
    </section>
  );
};

export default Hero;
